import { Group, Text, ComboboxProps, Button } from '@mantine/core';
import { DatesRangeValue } from '@mantine/dates';
import { FC } from 'react';

import styles from './DateFilterButton.module.css';
import baseStyles from '../../filter-button/BaseFilterButton.module.css';
import { BaseFilterButton, useBaseFilterButton } from 'components/filter-button/BaseFilterButton';
import { formatDateRange, isDateRangeEmpty } from './dateFilterUtils';
import { DatePicker } from 'components/dates/date-picker/DatePicker';

export type DateRangeFilterButtonProps = {
    label: string;
    disabled?: boolean;
    value?: DatesRangeValue;
    defaultValue?: DatesRangeValue;
    submittedValue?: DatesRangeValue;
    onChange?: (value: DatesRangeValue) => void;
    onSubmit?: (value: DatesRangeValue) => void;
    showSubmitButtons?: boolean;
    size?: string;
    form?: string;
    comboboxProps?: Partial<ComboboxProps>;
    placeholder?: string;
    clearable?: boolean;
    dateFormat?: Intl.DateTimeFormatOptions;
};

export const DateRangeFilterButton: FC<DateRangeFilterButtonProps> = ({
    label,
    disabled,
    value,
    defaultValue,
    submittedValue,
    onChange,
    onSubmit,
    form,
    size,
    showSubmitButtons = false,
    comboboxProps,
    placeholder = 'Select date range',
    clearable = true,
    dateFormat,
}) => {
    const hooks = useBaseFilterButton<DatesRangeValue>({
        value,
        defaultValue,
        submittedValue,
        onChange,
        onSubmit,
        finalValue: [null, null],
    });

    const { value: _value, setValue, submittedValue: _submittedValue } = hooks;

    const hasActiveFilters = !isDateRangeEmpty(_submittedValue);

    const handleDateChange = (dateRange: DatesRangeValue) => {
        setValue(dateRange);
    };

    const handleClear = () => {
        setValue([null, null]);
    };

    const hiddenInputs = (
        <>
            <input
                type="hidden"
                name={form ? `${form}-start-date` : undefined}
                value={_value?.[0]?.toLocaleString() || ''}
                disabled={disabled}
            />
            <input
                type="hidden"
                name={form ? `${form}-end-date` : undefined}
                value={_value?.[1]?.toLocaleString() || ''}
                disabled={disabled}
            />
        </>
    );

    return (
        <BaseFilterButton
            label={label}
            disabled={disabled}
            size={size}
            showSubmitButtons={showSubmitButtons}
            comboboxProps={{
                middlewares: { flip: false, shift: false },
                ...comboboxProps,
            }}
            hasActiveFilters={hasActiveFilters}
            dropdownClassName={styles.dropdown}
            triggerClassName={styles.trigger}
            badgeClassName={baseStyles.dot}
            hooks={hooks}
            hiddenInputs={hiddenInputs}
        >
            <Group align="center" gap="sm" wrap="nowrap" justify="space-between" className={styles.topGroup}>
                <Text size="sm" className={styles.displayValue}>
                    {isDateRangeEmpty(_value) ? placeholder : formatDateRange(_value, dateFormat)}
                </Text>
                {clearable && hasActiveFilters && (
                    <Button
                        type="button"
                        onClick={handleClear}
                        className={styles.clearButton}
                        aria-label="Clear date range"
                    >
                        Clear
                    </Button>
                )}
            </Group>

            <div className={styles.datePickerWrapper}>
                <DatePicker type="range" value={_value} onChange={handleDateChange} aria-label={label} />
            </div>
        </BaseFilterButton>
    );
};
